import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import type { ConversationRole, ReasoningEffort } from '@jabberr/core';

/** Facts about the current conversation shown in the right-hand drawer. */
export interface DrawerInfo {
  contextVersion?: number;
  reasoningEffort?: ReasoningEffort;
  role?: ConversationRole;
  conversationKind?: 'chat' | 'review' | 'meeting';
  projectId?: string;
  /** Shown on the top bar instead of a conversation title. */
  title?: string;
  /** Items rendered in the top-bar "more" menu (hidden when empty). */
  actions?: TopBarAction[];
}

/** One item in the phone "More" menu. An item with a submenu opens it in place (with a way back) instead of acting. */
export interface TopBarAction {
  label: string;
  onSelect?: () => void;
  disabled?: boolean;
  submenu?: { label: string; description?: string; onSelect: () => void }[];
}

interface DrawerState {
  info: DrawerInfo;
  setInfo: (info: DrawerInfo) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
}

const DrawerContext = createContext<DrawerState | null>(null);

export function DrawerProvider({ children }: { children: ReactNode }): ReactNode {
  const [info, setInfo] = useState<DrawerInfo>({});
  const [open, setOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem('jabberr.drawer') === 'open';
    } catch {
      return false;
    }
  });
  const value = useMemo<DrawerState>(
    () => ({
      info,
      setInfo,
      open,
      setOpen: (o) => {
        setOpen(o);
        try {
          localStorage.setItem('jabberr.drawer', o ? 'open' : 'closed');
        } catch {
          // ignore
        }
      },
    }),
    [info, open],
  );
  return <DrawerContext.Provider value={value}>{children}</DrawerContext.Provider>;
}

export function useDrawer(): DrawerState {
  const ctx = useContext(DrawerContext);
  if (!ctx) throw new Error('useDrawer outside DrawerProvider');
  return ctx;
}
